import SafeIcon from '../common/SafeIcon';
import { comparisonRows } from '../data/moneyPageContent';

const features = [
  ['PhoneCall', 'Instant 24/7 Call Answering', 'Zero hold times. AutoReception answers on ring one, day or night.'],
  ['CalendarCheck', 'Automated Calendar Booking', 'Connect Google Calendar, Calendly, or your CRM to schedule appointments in real time.'],
  ['MessageCircle', 'Smart Qualification and FAQ Handling', 'Answer questions about services, pricing ranges, service areas, and business hours.'],
  ['Smartphone', 'Instant Missed Call Text-Back', 'Automatically send an SMS with booking links or intake forms when a caller drops off.'],
  ['FileText', 'Call Transcripts and CRM Sync', 'Send summaries, recordings, and extracted lead data through email, SMS, Zapier, or webhooks.']
];

export const ProblemSection = () => (
  <section className="border-y border-slate-800 bg-slate-900/50 px-4 py-20 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <p className="mb-4 text-sm font-bold uppercase tracking-widest text-red-400">The cost of silence</p>
          <h2 className="text-4xl font-extrabold text-white">Every missed call is a revenue decision.</h2>
        </div>
        <div className="space-y-5 text-lg leading-relaxed text-slate-300">
          <p>More than <strong className="text-white">62% of calls to small businesses go unanswered</strong>. Of those callers, roughly 85% hang up and call a competitor immediately.</p>
          <p>You can pay <strong className="text-white">$1,500–$3,500 per month</strong> for a human receptionist, or lose thousands in uncaptured revenue every week. AutoReception gives you a third option: consistent, immediate coverage at a predictable monthly cost.</p>
        </div>
      </div>
    </div>
  </section>
);

export const FeaturesSection = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-14 max-w-2xl">
        <p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Built for conversion</p>
        <h2 className="text-4xl font-extrabold text-white">A virtual AI receptionist that does more than answer.</h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {features.map(([icon, title, description]) => (
          <div key={title} className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7 transition hover:-translate-y-1 hover:border-blue-500/50">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10">
              <SafeIcon name={icon} className="text-xl text-blue-400" />
            </div>
            <h3 className="mb-3 text-xl font-bold text-white">{title}</h3>
            <p className="leading-relaxed text-slate-400">{description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ComparisonSection = () => (
  <section className="bg-slate-900/50 px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-extrabold text-white">More coverage. Less overhead.</h2>
        <p className="mx-auto mt-4 max-w-2xl text-slate-400">Compare automated call answering for local business with the alternatives.</p>
      </div>
      <div className="overflow-x-auto rounded-3xl border border-slate-800">
        <table className="min-w-[760px] w-full text-left">
          <thead className="bg-slate-800 text-sm uppercase tracking-wider text-slate-300">
            <tr><th className="p-5">Feature / Metric</th><th className="p-5 text-blue-300">AutoReception AI</th><th className="p-5">Traditional Human Receptionist</th><th className="p-5">Standard Voicemail</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800 bg-slate-900/80">
            {comparisonRows.map((row) => <tr key={row[0]} className="text-slate-300"><th className="p-5 font-semibold text-white">{row[0]}</th><td className="p-5 font-semibold text-emerald-400">{row[1]}</td><td className="p-5">{row[2]}</td><td className="p-5 text-slate-500">{row[3]}</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export const HowItWorksSection = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-14 text-center"><h2 className="text-4xl font-extrabold text-white">Go live in three practical steps.</h2></div>
      <div className="grid gap-6 md:grid-cols-3">
        {[
          ['01', 'Forward Your Number', 'Keep your existing business line. Forward missed calls, after-hours calls, or every call to your AutoReception number.'],
          ['02', 'Upload Your Business Knowledge', 'Add FAQs, services, pricing guidelines, service areas, and your calendar integration in minutes.'],
          ['03', 'Never Miss a Paying Client', 'AutoReception answers, qualifies, books, and logs every interaction automatically.']
        ].map(([number, title, description]) => (
          <div key={number} className="rounded-3xl border border-slate-800 bg-slate-900 p-7">
            <span className="text-5xl font-black text-blue-500/40">{number}</span>
            <h3 className="mt-5 text-xl font-bold text-white">{title}</h3>
            <p className="mt-3 leading-relaxed text-slate-400">{description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);